import React, { Suspense } from "react";
import { Route } from "react-router-dom";
import NotFound from "./Components/NotFound";
import Layout from "./Layout/Layout";
import Product from './Product/Product';
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { iconBaseUrl } from "./Common/Environment";
import { initializeFeatureFlags } from "./FeatureFlags";
import { DataAccessContextProvider } from "./Contexts/DataAccessContext";
import { ToastProvider } from "./Contexts/ToastContext";
import { CopilotBubbleProvider } from "./Contexts/CopilotBubbleContext";
import { hostedSites } from "./Routing/HostedSites";
import { HostedRoutes, resolveHostedSite } from "./Routing/HostnameRouting";

initializeIcons(iconBaseUrl);
initializeFeatureFlags(); // Initialize feature flags at module level

const Home = React.lazy(() => import("./Home/Home"));
const Products = React.lazy(() => import("./Products/Products"));
const Extensions = React.lazy(() => import("./Extensions/Extensions"));
const Extension = React.lazy(() => import("./Extension/Extension"));
const Blade = React.lazy(() => import("./Blade/Blade"));
const TestOverview = React.lazy(() => import("./TestHub/TestOverview"));
const TestHubHome = React.lazy(() => import("./TestHub/TestHubHome"));
const Runners = React.lazy(() => import("./TestHub/Runners/Runners"));
const EnvironmentConfig = React.lazy(() => import("./TestHub/EnvironmentConfig/EnvironmentConfig"));
const DeploymentGatesHome = React.lazy(() => import("./DeploymentGates/DeploymentGatesHome"));
const DeploymentGatesDetails = React.lazy(() => import("./DeploymentGates/DeploymentGateDetails"));
const BladeLoadTelemetryDetails = React.lazy(() => import("./DeploymentGates/BladeLoadTelemetryDetails"));
const TestRunOverview = React.lazy(() => import("./TestHub/TestRunOverview"));
const PullRequest = React.lazy(() => import("./PullRequests/PullRequest"));
const UnitTestCoverage = React.lazy(() => import("./CodeCoverage/Main"))
const ScenarioTest = React.lazy(() => import("./ScenarioTests/ScenarioTest"));
const ScenarioTestCase = React.lazy(() => import("./ScenarioTests/ScenarioTestCase/ScenarioTestCase"));
const ScenarioTestResult = React.lazy(() => import("./ScenarioTests/ScenarioTestResult/ScenarioTestResult"));
const ScenarioTestGateViewer = React.lazy(() => import("./ScenarioTests/ScenarioTestGateViewer"));
const ScenarioTestSummary = React.lazy(() => import("./ScenarioTests/ScenarioTestSummary/ScenarioTestSummary"));
const PartnerTestsMain = React.lazy(() => import("./PartnerTests/Main"));
const PartnerTestsDashboard = React.lazy(() => import("./PartnerTests/Dashboard"));
const PartnerTestsCreateRelease = React.lazy(() => import("./PartnerTests/CreateRelease"));
const PartnerTestsOnboarding = React.lazy(() => import("./PartnerTests/Onboarding"));
const BuildAndDeployment = React.lazy(() => import("./BuildAndDeployment/BuildAndDeployment"));
const BuildResultAndDeployment = React.lazy(() => import("./BuildAndDeployment/Builds/BuildOverview"));
const Controllers = React.lazy(() => import("./Controllers/Controllers"));
const Session = React.lazy(() => import("./Session/Session"));
const CorrelationId = React.lazy(() => import("./CorrelationId/CorrelationId"));
const CorrelationIdInput = React.lazy(() => import("./CorrelationId/CorrelationIdInput"));
const CopilotConversation = React.lazy(() => import("./CopilotConversation/CopilotConversation"));
const CopilotConversationInput = React.lazy(() => import("./CopilotConversation/CopilotConversationInput"));
const User = React.lazy(() => import("./User/User"));
const Tenant = React.lazy(() => import("./Tenants/Tenant"));
const DataCenters = React.lazy(() => import("./DataCenters/DataCenters"));
const DtSecurityWave1 = React.lazy(() => import("./Security/DtSecurityWave1/DtSecurityWave1"));
const SFI = React.lazy(() => import("./Security/SFI/SFI"));
const Livesite = React.lazy(() => import("./Livesite/Livesite"));
const TenantManagement = React.lazy(() => import("./TenantManagement/TenantManagement"));
const Axq = React.lazy(() => import("./AzureExperienceQuality/Axq"));
const AxlsHome = React.lazy(() => import("./Axls/Home"));
const GitHubAuthorize = React.lazy(() => import("./GitHubAuthorize/GitHubAuthorize"));
const DataGridDemo = React.lazy(() => import("./Components/DataGrid/DataGridDemo"));

export const testHubRoute = "/iaasexp/testhub";

const App = () => {
    const [title, setTitle] = React.useState<string>("Loading...");
    const site = resolveHostedSite(window.location.hostname, hostedSites);

    React.useEffect(() => {
        document.title = title + " - " + (site?.name || "Extension Analyzer");
    }, [title, site]);

    return (
        <DataAccessContextProvider>
            <CopilotBubbleProvider>
                <Layout title={title} site={site}>
                    <Suspense fallback="Loading...">
                        <ToastProvider>
                            <HostedRoutes site={site} sharedPaths={["/github-authorize"]}>
                                <Route path="/" element={<Home setTitle={setTitle} />} />
                                <Route path="/products" element={<Products setTitle={setTitle} />} />
                                <Route path="/products/:productName" element={<Product setTitle={setTitle} />} />
                                <Route path="/extensions" element={<Extensions setTitle={setTitle} />} />
                                <Route path="/extensions/:extensionName" element={<Extension setTitle={setTitle} />} />
                                <Route path="/extensions/:extensionName/blades/:bladeName" element={<Blade setTitle={setTitle} />} />
                                <Route path="/iaasexp/coverage/:coverageType/*" element={<UnitTestCoverage setTitle={setTitle} />} />
                                <Route path={testHubRoute} element={<TestHubHome setTitle={setTitle} />} />
                                <Route path="/iaasexp/ctestdash" element={<TestOverview setTitle={setTitle} />} />
                                <Route path="/iaasexp/ctestdash/testrun/:jobId" element={<TestRunOverview setTitle={setTitle} />} />
                                <Route path="/iaasexp/ctest/environmentConfig" element={<EnvironmentConfig setTitle={setTitle} />} />
                                <Route path="/iaasexp/pullRequests/:prId" element={<PullRequest setTitle={setTitle} />} />
                                <Route path="/iaasexp/deploymentgates" element={<DeploymentGatesHome setTitle={setTitle} />} />
                                <Route path="/iaasexp/deploymentgates/:buildNumber/extension/:extension" element={<DeploymentGatesDetails setTitle={setTitle} />} />
                                <Route path="/iaasexp/deploymentgates/:buildNumber/extension/:extension/stage/:stage/timestamp/:timestamp" element={<BladeLoadTelemetryDetails setTitle={setTitle} />} />
                                <Route path="/iaasexp/scenarioTests" element={<ScenarioTest setTitle={setTitle} />} />
                                {/* Suite names can contain slashes (e.g. parent/child), so we use a splat route and parse params manually in the component */}
                                <Route path="/iaasexp/scenariotestresult/suites/*" element={<ScenarioTestResult setTitle={setTitle} />} />
                                <Route path="/iaasexp/scenariotestresult/testcases/:testCaseName" element={<ScenarioTestCase setTitle={setTitle} />} />
                                <Route path="/iaasexp/scenarioTests/:runId" element={<ScenarioTest setTitle={setTitle} />} />
                                <Route path="/iaasexp/scenarioTestResult" element={<ScenarioTestSummary setTitle={setTitle} />} />
                                <Route path="/iaasexp/buildAndDeployments" element={<BuildAndDeployment setTitle={setTitle} />} />
                                <Route path="/iaasexp/buildNumber/:buildNumber/buildId/:buildId" element={<BuildResultAndDeployment setTitle={setTitle} />} />
                                <Route path="/iaasexp/runners" element={<Runners setTitle={setTitle} />} />
                                <Route path="/iaasexp/controllers" element={<Controllers setTitle={setTitle} />} />
                                <Route path="/iaasexp/scenariotestgateviewer" element={<ScenarioTestGateViewer setTitle={setTitle} />} />
                                <Route path="/partnertests" element={<PartnerTestsMain setTitle={setTitle} />} />
                                <Route path="/partnertests/dashboard" element={<PartnerTestsDashboard setTitle={setTitle} />} />
                                <Route path="/partnertests/create-release" element={<PartnerTestsCreateRelease setTitle={setTitle} />} />
                                <Route path="/partnertests/onboarding" element={<PartnerTestsOnboarding setTitle={setTitle} />} />
                                <Route path="/session" element={<Session setTitle={setTitle} />} />
                                <Route path="/user" element={<User setTitle={setTitle} />} />
                                <Route path="/diagnose/correlationid" element={<CorrelationIdInput setTitle={setTitle} />} />
                                <Route path="/diagnose/correlationid/:correlationId" element={<CorrelationId setTitle={setTitle} />} />
                                <Route path="/diagnose/copilot" element={<CopilotConversationInput setTitle={setTitle} />} />
                                <Route path="/diagnose/copilot/:conversationId" element={<CopilotConversation setTitle={setTitle} />} />
                                <Route path="/tenants/:tenantId" element={<Tenant setTitle={setTitle} />} />
                                <Route path="/datacenters" element={<DataCenters setTitle={setTitle} />} />
                                <Route path="/security/dtWave1" element={<DtSecurityWave1 setTitle={setTitle} />} />
                                <Route path="/security/sfi" element={<SFI setTitle={setTitle} />} />
                                <Route path="/livesite" element={<Livesite setTitle={setTitle} />} />
                                <Route path="/tenantManagement" element={<TenantManagement setTitle={setTitle} />} />
                                <Route path="/axq" element={<Axq setTitle={setTitle} />} />
                                <Route path="/axls">
                                    <Route index element={<AxlsHome setTitle={setTitle} />} />
                                    <Route path="*" element={<NotFound />} />
                                </Route>
                                <Route path="/github-authorize" element={<GitHubAuthorize />} />
                                <Route path="/demo/datagrid" element={<DataGridDemo setTitle={setTitle} />} />
                                <Route path="*" element={<NotFound />} />
                            </HostedRoutes>
                        </ToastProvider>
                    </Suspense>
                </Layout>
            </CopilotBubbleProvider>
        </DataAccessContextProvider>
    );
};

export default React.memo(App);
